import React, { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { getProgramDetailsGfcRequest } from "src/redux/slices/programSlice";
import {
  isMilestoneLocked,
  needsMilestoneDocuments,
  parsePeriod,
} from "src/constants/milestones";
import { useBenefitAction } from "src/hooks/useBenefitAction";
import { Milestone } from "src/types/program.types";
import Card from "../Card/Card";
import BenefitRow from "../program/BenefitRow";
import MilestoneDocumentsPrompt from "../program/MilestoneDocumentsPrompt";
import { ChevronRight, Lock, Zap } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

interface ProgramDetailsCardProps {
  type: string;
}

const ProgramDetailsCard: React.FC<ProgramDetailsCardProps> = ({ type }) => {
  const navigate = useNavigate();
  const params = useParams();
  const supportProgramKey = params["support-program-key"];
  const dispatch = useAppDispatch();
  const { programDetails } = useAppSelector((state) => state.program);

  useEffect(() => {
    new Promise((resolve, reject) => {
      dispatch(
        getProgramDetailsGfcRequest({
          resolve,
          reject,
        }),
      );
    }).catch((error) => {
      console.error("Error while fetching GFC program details", error);
    });
  }, [dispatch]);

  const handleRightIconClick = () => {
    navigate(`/app/${supportProgramKey}/program-details`);
  };

  const handleBenefitAction = useBenefitAction();

  const handleMilestoneDocuments = (milestone: Milestone) => {
    navigate(
      `/app/${supportProgramKey}/program-details/milestone-documents/${milestone.key}`,
    );
  };

  const activeMilestone = programDetails.data?.milestones.find(
    (milestone) => milestone.isActive,
  );

  if (!activeMilestone) {
    return null;
  }

  const { label: periodLabel, value: periodValue } = parsePeriod(
    activeMilestone.period,
  );
  const isLocked = isMilestoneLocked(activeMilestone);
  const docsRequired = needsMilestoneDocuments(activeMilestone);
  // Locked or awaiting documents — nothing here is claimable yet, so the
  // benefit rows are dimmed and only the gate's own action stays live.
  const isGated = isLocked || docsRequired;
  const benefitCount = activeMilestone.benefits.length;

  return (
    <div className="mx-4">
      <Card
        title="Program Details"
        description="See how far you have come"
        icon={<Zap className="w-4 h-4" />}
        rightIcon={<ChevronRight className="w-4 h-4" />}
        rightIconClick={handleRightIconClick}
      >
        <div className="flex flex-col gap-3">
          {/* Header: which month, how many benefits, and the milestone's state */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-10 h-10 rounded-md overflow-hidden shrink-0 shadow-sm flex flex-col">
                <div
                  className="h-3.5 flex items-center justify-center text-[8px] font-bold uppercase tracking-wide"
                  style={{
                    backgroundColor: "var(--app-primary-light)",
                    color: "var(--app-primary)",
                  }}
                >
                  {periodLabel}
                </div>
                <div
                  className="flex-1 flex items-center justify-center text-sm font-bold"
                  style={{
                    backgroundColor: "var(--app-primary)",
                    color: "#ffffff",
                  }}
                >
                  {periodValue}
                </div>
              </div>
              <h3
                className="text-sm font-semibold"
                style={{
                  color: isGated
                    ? "var(--disabled-text-color)"
                    : "var(--card-title-color)",
                }}
              >
                {benefitCount} {benefitCount > 1 ? "benefits" : "benefit"} this
                month
              </h3>
            </div>

            {/* Availed is shown per benefit, not for the milestone as a whole */}
            {isGated && (
              <div
                className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center"
                style={{ backgroundColor: "var(--card-border)" }}
              >
                <Lock
                  className="w-3.5 h-3.5"
                  style={{ color: "var(--disabled-text-color)" }}
                />
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2">
            {activeMilestone.benefits.map((benefit) => (
              <BenefitRow
                key={benefit.name || benefit.title}
                milestone={activeMilestone}
                benefit={benefit}
                onAction={(action) =>
                  handleBenefitAction(activeMilestone, benefit, action)
                }
              />
            ))}
          </div>

          {isLocked && (
            <p
              className="text-xs font-medium"
              style={{ color: "var(--disabled-text-color)" }}
            >
              This will be activated in {periodLabel} {periodValue}
            </p>
          )}

          {/* Documents gate the whole milestone, so it's one prompt for the
              card rather than one per benefit. */}
          {docsRequired && (
            <MilestoneDocumentsPrompt
              milestone={activeMilestone}
              variant="card"
              onAction={() => handleMilestoneDocuments(activeMilestone)}
            />
          )}
        </div>
      </Card>
    </div>
  );
};

export default ProgramDetailsCard;
